import React, { useEffect, useMemo, useState } from "react";
import { useGetCompanySettings, useUpdateCompany } from "../queries/queries";
import {
   Spinner,
   SettingsPage,
   Box,
   H3,
   Typography,
   useI18nContext,
   Flex,
   Switch,
   Label,
   DateSelect,
   Button,
} from "@procore/core-react";
import { getAttachedDate, getDetachedDay } from "@laborchart-modules/common/dist/datetime";
import { isBefore, isSameDay } from "date-fns";

type COMPANY_SANDBOX_FORM = {
   scheduled_sandbox_refresh?: null | number;
   monthly_sandbox_refresh?: boolean;
};
const EMPTY_FORM = {
   scheduled_sandbox_refresh: null,
   monthly_sandbox_refresh: false,
};

export const SandboxContainer = () => {
   const I18n = useI18nContext();
   const { data: company, isLoading } = useGetCompanySettings();
   const { mutate, isPending } = useUpdateCompany();

   const [form, setForm] = useState<COMPANY_SANDBOX_FORM>(EMPTY_FORM);
   const [savedForm, setSavedForm] = useState<COMPANY_SANDBOX_FORM>(EMPTY_FORM);

   useEffect(() => {
      if (!company) return;
      const values = {
         scheduled_sandbox_refresh: company.scheduled_sandbox_refresh,
         monthly_sandbox_refresh: company.monthly_sandbox_refresh,
      };
      setForm(values);
      setSavedForm(values);
   }, [company]);

   const isDirty = JSON.stringify(form) !== JSON.stringify(savedForm);

   const handleCancel = () => {
      setForm(savedForm);
   };

   const scheduledRefreshDateDisplay = useMemo(() => {
      if (form.scheduled_sandbox_refresh) {
         return getAttachedDate(form.scheduled_sandbox_refresh);
      }
      return undefined;
   }, [form]);

   return (
      <SettingsPage.Card>
         <SettingsPage.Section>
            <Box padding="lg">
               <Spinner loading={isLoading}>
                  <H3>
                     {I18n.t("views.company.workforce_planning.settings.sandbox.sandbox_title")}
                  </H3>
                  <Flex direction="row" gap="xl">
                     <Flex gap="md" paddingTop="md" flex="1">
                        <Switch
                           checked={form.monthly_sandbox_refresh}
                           onChange={() =>
                              setForm((prev) => ({
                                 ...prev,
                                 monthly_sandbox_refresh: !prev.monthly_sandbox_refresh,
                              }))
                           }
                           aria-label={
                              form.monthly_sandbox_refresh
                                 ? I18n.t(
                                      "views.company.workforce_planning.settings.sandbox.refresh_toggle_enabled",
                                   )
                                 : I18n.t(
                                      "views.company.workforce_planning.settings.sandbox.refresh_toggle_disabled",
                                   )
                           }
                        />
                        <Typography>
                           {I18n.t(
                              "views.company.workforce_planning.settings.sandbox.monthly_refresh_toggle",
                           )}
                        </Typography>
                     </Flex>
                     {form.monthly_sandbox_refresh && (
                        <Flex direction="column" gap="md" flex="1">
                           <Label htmlFor="scheduled_sandbox_refresh">
                              {I18n.t(
                                 "views.company.workforce_planning.settings.sandbox.scheduled_refresh_date",
                              )}
                           </Label>
                           <DateSelect
                              value={scheduledRefreshDateDisplay}
                              // As any is temporary placeholder while we wait for updated types from lc-core-api
                              onChange={(value) => {
                                 const newValue = value ? getDetachedDay(new Date(value)) : null;
                                 setForm((prev) => ({
                                    ...prev,
                                    scheduled_sandbox_refresh: newValue,
                                 }));
                              }}
                              disabledDate={(date, today) =>
                                 isBefore(date, today) || isSameDay(date, today)
                              }
                              aria-label={
                                 scheduledRefreshDateDisplay
                                    ? I18n.t(
                                         "views.company.workforce_planning.settings.sandbox.scheduled_refresh_date_set",
                                         { date: scheduledRefreshDateDisplay.toDateString() },
                                      )
                                    : I18n.t(
                                         "views.company.workforce_planning.settings.sandbox.no_scheduled_refresh_date",
                                      )
                              }
                           />
                        </Flex>
                     )}
                  </Flex>
                  {isDirty && (
                     <Flex paddingTop="md" gap="sm" justifyContent="flex-end">
                        <Button variant="tertiary" onClick={handleCancel} disabled={isPending}>
                           {I18n.t(
                              "views.company.workforce_planning.settings.account_settings.cancel",
                           )}
                        </Button>
                        <Button variant="primary" onClick={() => mutate(form)} loading={isPending}>
                           {I18n.t(
                              "views.company.workforce_planning.settings.account_settings.save",
                           )}
                        </Button>
                     </Flex>
                  )}
               </Spinner>
            </Box>
         </SettingsPage.Section>
      </SettingsPage.Card>
   );
};
